// #SPLIT_YARD_V1 — shared helpers, moved out of FleetModule.js unchanged so it and YardModule.js both use them.
import { Fragment, useState, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";

export const pad2 = (n) => String(n).padStart(2,'0');

export const iso = (d) => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;

export const mondayOf = (d) => { const x=new Date(d); const b=(x.getDay()+6)%7; x.setDate(x.getDate()-b); x.setHours(0,0,0,0); return x; };

export const addDays = (d,n) => { const x=new Date(d); x.setDate(x.getDate()+n); return x; };

// Bill: "Days should be MM/DD on header." Zero-padded so the six day tabs are the same width and
// the eye can scan a column of dates instead of a ragged one.
export const fmtMD = (d) => `${pad2(d.getMonth()+1)}/${pad2(d.getDate())}`;

// #FLEET_WEEKROLLUP_WKLABEL_V1 (2026-08-26) — Bill: "add Week ending Date, WK #." Standard ISO-8601
// week number (Monday-start weeks, week 1 = the week containing the year's first Thursday) — same
// numbering everyone's calendar/payroll software already uses, so "WK 35" means the same thing here.
export const isoWeekNum = (d) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - firstDayNum + 3);
  return 1 + Math.round((t - firstThu) / (7 * 86400000));
};

export const money = (n) => '$' + (Math.round(Number(n)||0)).toLocaleString();

// Bill: "Cost to include cents and placement of $ sign so its even."
// Accounting form — the $ is pinned to the left of the cell and the figure is right-aligned, so a
// column of $250.00 / $1,250.00 / $95.50 lines up on both the sign and the decimal point.
export const cents = (n) => (Number(n)||0).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 });

export const money2 = (n) => '$' + cents(n);

// #FLEET_DATE_V1 — Bill: "Dates (Should be MM/DD/YY)". The master carries ISO (2024-03-01) from the
// import and MM/DD/YY from hand entry. Display is always MM/DD/YY; anything unparseable is shown
// verbatim rather than blanked, because a cell reading "TBD" is information and an empty one is not.
export const fmtDate = (v) => {
  const s = String(v == null ? '' : v).trim();
  if (!s) return '';
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${pad2(+m[2])}/${pad2(+m[3])}/${String(m[1]).slice(2)}`;
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) return `${pad2(+m[1])}/${pad2(+m[2])}/${m[3].length === 4 ? m[3].slice(2) : m[3]}`;
  return s;
};

export const num = (v) => { const n=parseFloat(String(v==null?'':v).replace(/[^0-9.-]/g,'')); return isNaN(n)?0:n; };

// #FLEET_YARD_PORTAL_THEME_V1 (2026-09-01) — Bill: pill-shaped nav buttons with the same bright glow
// used elsewhere in the app (App.js/PMDashboard.js's own `glow` helper — identical formula, reused
// here so it reads as one system), plus a rustic riveted-steel-plate treatment for the Yard portal
// specifically ("he manages the steel for sure") — a faint brushed-metal band behind the module header
// and four corner rivets on the panels he's looking at all day, without reskinning every Fleet screen.
export const glow = (c) => ({ boxShadow: `0 0 0 1px ${c}22, 0 8px 24px -10px ${c}` });

export const RIVET_D = 7;

// Bill: "add Rivets to all his tables and some could be silver or bolts maybe mix it up a little" —
// three metal tones (gunmetal steel / brushed silver / brass) and two head shapes (round rivet / hex
// bolt with a slot) so the corner hardware isn't one repeated sticker on every panel. Each call site
// below picks its own tone/kind; defaults reproduce the original steel rivet exactly.
export const RIVET_TONES = {
  steel:  { hi:'#6B7078', lo:'#2A2D32' },
  silver: { hi:'#E8ECF0', lo:'#9AA3AC' },
  brass:  { hi:'#E8C273', lo:'#8A6A2A' },
};

export const dotStyleBase = (tone) => {
  const t = RIVET_TONES[tone] || RIVET_TONES.steel;
  return { background:`radial-gradient(circle at 35% 30%, ${t.hi}, ${t.lo} 70%)`,
    boxShadow:'0 1px 1px rgba(0,0,0,0.6), inset 0 0.5px 0 rgba(255,255,255,0.25)' };
};

export const boltStyleBase = (tone) => {
  const t = RIVET_TONES[tone] || RIVET_TONES.steel;
  return { clipPath:'polygon(25% 0%,75% 0%,100% 50%,75% 100%,25% 100%,0% 50%)',
    background:`linear-gradient(135deg, ${t.hi}, ${t.lo})`, boxShadow:'0 1px 1px rgba(0,0,0,0.6)' };
};

export const RivetDot = ({ pos, tone='steel', kind='rivet' }) => kind === 'bolt' ? (
  <span style={{ position:'absolute', ...pos, width:RIVET_D+1, height:RIVET_D+1, pointerEvents:'none' }}>
    <span style={{ position:'absolute', inset:0, ...boltStyleBase(tone) }} />
    <span style={{ position:'absolute', top:'48%', left:'12%', width:'76%', height:1, background:'rgba(0,0,0,0.55)', transform:'rotate(20deg)' }} />
  </span>
) : (
  <span style={{ position:'absolute', ...pos, width:RIVET_D, height:RIVET_D, borderRadius:'50%', pointerEvents:'none', ...dotStyleBase(tone) }} />
);

// `header` variant only places the two RIGHT-side rivets — the header row's left side is the DDD
// logo, and rivets there collided with it (Bill: "plugged through LOGO"). Panels (no logo) keep all 4.
export const RivetCorners = ({ header=false, tone='steel', kind='rivet' }) => (
  <Fragment>
    {!header && <RivetDot pos={{ top:6, left:6 }} tone={tone} kind={kind} />}
    <RivetDot pos={{ top:6, right:6 }} tone={tone} kind={kind} />
    {!header && <RivetDot pos={{ bottom:6, left:6 }} tone={tone} kind={kind} />}
    <RivetDot pos={{ bottom:6, right:6 }} tone={tone} kind={kind} />
  </Fragment>
);

// #FLEET_AUTOCOMPLETE_V1 (2026-09-02) — Bill: mobile browsers (iOS Safari especially) mostly don't
// render native <input list="..."> datalist suggestions at all, even though the identical markup
// shows a dropdown fine on desktop Chrome. This is a real dropdown Claude controls directly — same
// behavior on every device — used everywhere a Sup/Job picker used to be a native datalist. Rendered
// through a portal straight onto <body> so it is never clipped by a scrolling table ancestor, and
// repositioned on scroll/resize while open. `options` is [{value, label, sub?}], built ONCE by the
// caller (not per-row) so 300 Daily Inventory rows stay cheap — only the focused row's list filters,
// same reasoning as the single shared <datalist> this replaces.
export function AutoCompleteInput({ options, value, onChange, disabled, placeholder, title, style, onFocus, onBlur }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [hi, setHi] = useState(-1);
  const ref = useRef(null);

  const q = String(value || '').trim().toLowerCase();
  const filtered = useMemo(() => {
    const base = options || [];
    if (!q) return base.slice(0, 60);
    return base.filter(o => o.label.toLowerCase().includes(q) || String(o.sub||'').toLowerCase().includes(q)).slice(0, 60);
  }, [options, q]);

  const place = () => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 160) });
  };
  const openList = () => { if (disabled) return; place(); setOpen(true); setHi(-1); };
  const closeList = () => { setOpen(false); setHi(-1); };

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => { window.removeEventListener('scroll', onMove, true); window.removeEventListener('resize', onMove); };
  }, [open]);

  const pick = (opt) => { onChange(opt.value); closeList(); };

  return (
    <div style={{ position:'relative', width:'100%' }}>
      <input ref={ref} value={value||''} disabled={disabled} placeholder={placeholder} title={title} style={style}
        onChange={e => { onChange(e.target.value); openList(); }}
        onFocus={e => { openList(); onFocus && onFocus(e); }}
        onBlur={e => { onBlur && onBlur(e); window.setTimeout(closeList, 150); }}
        onKeyDown={e => {
          if (!open) return;
          if (e.key==='ArrowDown') { e.preventDefault(); setHi(h => Math.min(h+1, filtered.length-1)); }
          else if (e.key==='ArrowUp') { e.preventDefault(); setHi(h => Math.max(h-1, 0)); }
          else if (e.key==='Enter') { if (hi>=0 && filtered[hi]) { e.preventDefault(); pick(filtered[hi]); } }
          else if (e.key==='Escape') { closeList(); }
        }} />
      {open && filtered.length > 0 && pos && createPortal(
        <div style={{ position:'fixed', top:pos.top, left:pos.left, width:pos.width, zIndex:5000,
          background:'#1C2024', border:'1px solid #3A3F45', borderRadius:8, maxHeight:220, overflowY:'auto',
          boxShadow:'0 8px 24px rgba(0,0,0,0.55)' }}>
          {filtered.map((o,i) => (
            <div key={o.value+'|'+i} onMouseDown={e => { e.preventDefault(); pick(o); }}
              style={{ padding:'6px 10px', fontSize:12.5, cursor:'pointer', color:'#EDEEF0',
                background: i===hi ? '#262B30' : 'transparent' }}>
              {o.label}{o.sub ? <span style={{ color:'#98A2AA', marginLeft:6, fontSize:11 }}>{o.sub}</span> : null}
            </div>
          ))}
        </div>, document.body
      )}
    </div>
  );
}

// #YARD_CONSIGN_V1 (2026-09-30) — Bill: "YARD: Need a consignment Button and at least 3 buckets.
// Here is one from Vermeer and we will have another for Ditch Witch, MSC Filters & tires soon."
// Vendor-owned parts sitting on DUC's shelves — NOT DUC inventory, never counted in on-hand value
// (same rule Chemicals already applies to V CSGN / D CSGN, #FLEET_CHEM_CSGNFIX_V1). One bucket per
// vendor; Vermeer, Ditch Witch and MSC (Filters & Tires) are the fixed three, "+ Bucket" adds more.
// Vermeer self-seeds once from its 09/25/26 Customer Consignment Inventory report (acct DAKOT006,
// branch 01 VT IRVING — 243 lines, $153,854.57, ties to the report's TOTALS line to the penny).
//
// #YARD_CONSIGN_V2 (2026-09-30) — Bill, after V1 landed: the vendor's QTY is the CEILING (what we
// should/expect to have), not what's on the shelf ("Bore Commander he shows 3 5GAL & 8 1GAL but the
// list shows far more"). So:
//   • Ceiling = vendor report qty (unchanged, still what Paste report updates).
//   • On Hand = free-write actual count Jerry keeps (persists; replaces V1's temporary Count mode).
//   • Chemicals already tracks V CSGN / D CSGN product on hand — for those lines Chemicals is the
//     truth: On Hand is read live from `fleet-chem-inventory` (auto-linked by vendor code + exact
//     each-price, or pinned by hand in Edit), shown read-only here with a CHEM tag.
//   • Pull → Unit: parts removed get assigned to a unit off the Equipment Master; qty comes off On
//     Hand and lands in the Pulls log — consigned stock pulled is charged to Dakota.
//   • Order Request: To Order = Ceiling − On Hand; builds a letterhead request (on hand, value on
//     hand, what's expected to be ordered) that sends from the system via /api/send-email (same
//     Graph path as #FLEET_CHEM_ORDERSEND_V1) or prints. Mark Received adds the qty back to On Hand.
//   • $ cells match Chemicals (ChemMoney): $ pinned against the figure, right-aligned, tabular.
// Storage: `fleet-yard-consignment` = { buckets:[{id,name,acct,branch,asOf,color,to[]}],
//   items:{[bucketId]:[{id,part,bin,desc,qty(=ceiling),unit,onHand?,ohAt?,chemLink?}]},
//   pulls:[{id,date,bucketId,itemId,part,desc,qty,each,unitNo,note,by}],
//   orders:[{id,bucketId,createdAt,by,po,note,to[],lines[],onHandValue,orderValue,status,receivedAt?}],
//   seeded:{vermeer:true, mscSplit:true, msc:true} }
//
// #YARD_CONSIGN_SPLIT_V1 (2026-10-06) — Bill: "break out Filter & Tires into one bucket and move MSC into
// its own bucket." The old 'msc' bucket ("MSC · Filters & Tires") only ever held filters & tires (O'Reilly,
// Construction Edge, SW International…), so on first load its lines, pulls and orders move to a new
// 'filters' bucket untouched (same item ids, On Hand kept) and 'msc' becomes MSC alone. MSC self-seeds from
// its 10/01/24–10/01/26 sales history (acct 379754): Part # + each (latest year's average price) + how
// often we've ordered it. Left OUT per Bill: anything ordered only once in the two years, and no-charge
// lines (bins/trays). That report has no shelf quantities, so MSC lines start with NO ceiling — blank
// ceiling = no order suggested until someone sets one in Edit.
//
// #YARD_CONSIGN_NODASH_V1 (2026-10-06) — Bill: strip the "-" MSC puts in front of 15 part numbers (Excel reads
// it as a formula in the CSV). Rule for every bucket now: a part # never starts with a dash — stripped on the
// one-time load pass, on Paste report and on + Add part / Edit. MSC's original stays on the line as vPart.
export const CONSIGN_KEY = 'fleet-yard-consignment';

export const _csgnNum = (v) => { const n = Number(String(v==null?'':v).replace(/[$,\s]/g,'')); return isFinite(n) ? n : 0; };

export const _csgnHas = (v) => v !== undefined && v !== null && v !== '';

export const consignTotal = (list) => (list||[]).reduce((s,r) => s + _csgnNum(r.qty) * _csgnNum(r.unit), 0);
